import { useEffect, useRef, useState } from 'react'
import {
  CircledIcon,
  GpsFixIcon,
  UpdateSample,
  useErrorToast,
  useGetSampleByIdQuery,
  useUpdateSampleMutation,
  longitudeLatitudeToGeometry,
  ModalDialog,
  Mtds,
  XIcon,
  CheckIcon
} from 'prove-common'
import 'ol/ol.css'
import Map from 'ol/Map'
import View from 'ol/View'
import TileLayer from 'ol/layer/Tile'
import OSM from 'ol/source/OSM'
import { fromLonLat, toLonLat } from 'ol/proj'
import { Coordinate } from 'ol/coordinate'
import VectorSource from 'ol/source/Vector'
import VectorLayer from 'ol/layer/Vector'
import { Fill, Stroke, Style } from 'ol/style'
import CircleStyle from 'ol/style/Circle'
import { Point } from 'ol/geom'
import { Feature, MapBrowserEvent } from 'ol'
import BaseEvent from 'ol/events/Event.js'

type ModalProps = {
  isManualModalOpen?: boolean
  setIsManualModalOpen: (isModalOpen: boolean) => void
  sampleId?: string
  newSample?: boolean
  position?: GeolocationPosition
  onSelectedLonLatChanged?: (lonLat: [number, number]) => void
  deferSave?: boolean
}

export const ManualLocationRegistrationModal = ({
  isManualModalOpen,
  setIsManualModalOpen,
  sampleId,
  newSample,
  position,
  onSelectedLonLatChanged,
  deferSave = false
}: ModalProps) => {
  const { data: sample } = useGetSampleByIdQuery(sampleId, newSample)
  const mapRef = useRef<Map | null>(null)
  const mapElementRef = useRef<HTMLDivElement>(null)
  const [selectedLonLat, setSelectedLonLat] = useState<Coordinate | null>(null)
  const updateSample = useUpdateSampleMutation()
  const showError = useErrorToast('lagre plasseringen')

  useEffect(() => {
    if (mapElementRef.current) {
      if (mapRef.current) {
        mapRef.current.setTarget()
        mapRef.current = null
      }

      const markerSource = new VectorSource()

      const markerLayer = new VectorLayer({
        source: markerSource,
        style: new Style({
          image: new CircleStyle({
            radius: 7,
            fill: new Fill({ color: 'rgba(51, 153, 255, 0.8)' }),
            stroke: new Stroke({ color: 'white', width: 2 })
          })
        })
      })

      let view: View
      if (position) {
        const coord: Coordinate = [
          position.coords.longitude,
          position.coords.latitude
        ]
        setSelectedLonLat(coord)
        view = new View({
          center: fromLonLat([
            position.coords.longitude,
            position.coords.latitude
          ]),
          zoom: 14
        })
      } else {
        view = new View({
          center: fromLonLat([12.307778, 63.990556]),
          zoom: 4
        })
      }

      const map = new Map({
        target: mapElementRef.current,
        layers: [
          new TileLayer({
            source: new OSM()
          }),
          markerLayer
        ],
        view: view
      })
      mapRef.current = map

      const clickHandler = (event: BaseEvent | Event) => {
        const mapEvent = event as MapBrowserEvent<PointerEvent>
        const coordinate = map.getEventCoordinate(mapEvent.originalEvent)
        const lonLat = toLonLat(coordinate)
        setSelectedLonLat(lonLat)
      }

      map.on('singleclick', clickHandler)

      return () => {
        if (map) {
          map.un('singleclick', clickHandler)
          map.setTarget()
        }
        mapRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (mapRef.current) {
      const markerSource = (
        mapRef.current.getLayers().item(1) as VectorLayer
      ).getSource() as VectorSource
      if (selectedLonLat) {
        markerSource.clear()
        const markerViewCoord = fromLonLat(selectedLonLat)
        const markerFeature = new Feature({
          geometry: new Point(markerViewCoord)
        })
        markerSource.addFeature(markerFeature)
      } else {
        markerSource.clear()
      }
    }
  }, [selectedLonLat])

  const onSavePosition = () => {
    if (selectedLonLat && deferSave) {
      if (onSelectedLonLatChanged) {
        onSelectedLonLatChanged([selectedLonLat[0], selectedLonLat[1]])
      }
      setIsManualModalOpen(false)
      return
    }

    if (selectedLonLat && sample && sampleId) {
      const taskId = sample.taskId
      const sampntType = sample.sampntType
      const sampntTypeId = sampntType?.id
      const sampleTypeId = sample.sampleType?.id
      const [longitude, latitude] = selectedLonLat
      const updatedSample: UpdateSample = {
        ...sample,
        taskId,
        sampntTypeId,
        sampleTypeId,
        accuratePosition: true,
        geometry: longitudeLatitudeToGeometry({ longitude, latitude })
      }
      updateSample
        .mutateAsync({
          sampleId,
          body: updatedSample,
          etag: sample.etag
        })
        .then(() => {
          setIsManualModalOpen(false)
        })
        .catch((e) => {
          showError(e)
        })
    } else if (selectedLonLat && !sample && sampleId) {
      if (onSelectedLonLatChanged) {
        onSelectedLonLatChanged([selectedLonLat[0], selectedLonLat[1]])
      }
      setIsManualModalOpen(false)
    } else {
      Mtds.toast.danger('Ingen posisjon valgt')
    }
  }

  return (
    <ModalDialog
      open={Boolean(isManualModalOpen)}
      onClose={() => {
        setIsManualModalOpen(false)
      }}
      className='h-screen w-screen'
    >
      <Mtds.Grid className='h-full w-full grid-rows-[auto_1fr_auto]'>
        <Mtds.Flex data-align={'center'}>
          <CircledIcon icon={<GpsFixIcon />} />
          <Mtds.Heading>Velg posisjon i kart</Mtds.Heading>
        </Mtds.Flex>
        <div ref={mapElementRef} />
        <Mtds.Flex data-justify={'end'}>
          <Mtds.Button
            data-variant={'secondary'}
            onClick={() => {
              console.log('Cancel button clicked, closing modal')
              setIsManualModalOpen(false)
            }}
          >
            <XIcon />
            Avbryt
          </Mtds.Button>
          <Mtds.Button data-variant={'primary'} onClick={onSavePosition}>
            <CheckIcon />
            Lagre posisjon
          </Mtds.Button>
        </Mtds.Flex>
      </Mtds.Grid>
    </ModalDialog>
  )
}
